import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Handshake,
  FileCheck,
  CircleDollarSign,
  Scale,
  Landmark,
  PiggyBank,
  Briefcase,
  CreditCard,
  Building,
  Shield,
  Wallet,
  Receipt,
  ArrowLeftRight,
  Globe,
  BadgePercent,
  Umbrella,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const HAS_LOGO_URL = 'https://customer-assets.emergentagent.com/job_holder-enterprise/artifacts/ea0sayna_1.5_Aplica%C3%A7%C3%A3o-de-Logotipo_Fundo-Escuro.jpg';

const Servicos = () => {
  const { t } = useLanguage();

  const mainServices = [
    {
      icon: <FileCheck className="h-8 w-8" />,
      titleKey: 'services.fiscalManagement',
      descKey: 'services.fiscalManagementDesc',
      path: '/gestao-fiscal',
      highlight: true
    },
    {
      icon: <CircleDollarSign className="h-8 w-8" />,
      titleKey: 'services.positiveTax',
      descKey: 'services.positiveTaxDesc',
      path: '/gestao-positiva',
      highlight: true
    },
    {
      icon: <Scale className="h-8 w-8" />,
      titleKey: 'services.liabilities',
      descKey: 'services.liabilitiesDesc',
      path: '/gestao-passivos',
      highlight: true
    },
    {
      icon: <Handshake className="h-8 w-8" />,
      titleKey: 'services.ma',
      descKey: 'services.maDesc',
      path: '/ma',
      highlight: true
    },
    {
      icon: <Landmark className="h-8 w-8" />,
      titleKey: 'services.credit',
      descKey: 'services.creditDesc',
      path: '/credito-operacoes',
      highlight: true
    },
    {
      icon: <Shield className="h-8 w-8" />,
      titleKey: 'services.legal',
      descKey: 'services.legalDesc',
      path: '/assessoria-juridica',
      highlight: true
    },
  ];

  const consultingServices = [
    {
      icon: <Briefcase className="h-6 w-6" />,
      titleKey: 'servicesPage.consulting.management',
      descKey: 'servicesPage.consulting.managementDesc'
    },
    {
      icon: <Calculator className="h-6 w-6" />,
      titleKey: 'servicesPage.consulting.accounting',
      descKey: 'servicesPage.consulting.accountingDesc'
    },
    {
      icon: <Shield className="h-6 w-6" />,
      titleKey: 'servicesPage.consulting.legal',
      descKey: 'servicesPage.consulting.legalDesc'
    },
    {
      icon: <Building className="h-6 w-6" />,
      titleKey: 'servicesPage.consulting.thirdSector',
      descKey: 'servicesPage.consulting.thirdSectorDesc'
    },
  ];

  const digitalServices = [
    {
      icon: <Wallet className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.accounts',
      descKey: 'servicesPage.digital.accountsDesc'
    },
    {
      icon: <Shield className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.escrow',
      descKey: 'servicesPage.digital.escrowDesc'
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.payments',
      descKey: 'servicesPage.digital.paymentsDesc'
    },
    {
      icon: <PiggyBank className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.baas',
      descKey: 'servicesPage.digital.baasDesc'
    },
    {
      icon: <Receipt className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.boletos',
      descKey: 'servicesPage.digital.boletosDesc'
    },
    {
      icon: <ArrowLeftRight className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.transfers',
      descKey: 'servicesPage.digital.transfersDesc'
    },
    {
      icon: <Globe className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.exchange',
      descKey: 'servicesPage.digital.exchangeDesc'
    },
    {
      icon: <BadgePercent className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.creditFinancing',
      descKey: 'servicesPage.digital.creditFinancingDesc'
    },
    {
      icon: <Umbrella className="h-6 w-6" />,
      titleKey: 'servicesPage.digital.insurance',
      descKey: 'servicesPage.digital.insuranceDesc'
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="servicos-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block"
            >
              {t('servicesPage.heroTag')}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mb-6"
            >
              {t('servicesPage.heroTitle')}{' '}
              <span className="gold-sheen">{t('servicesPage.heroHighlight')}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg text-stone-600"
            >
              {t('servicesPage.heroDesc')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Services - Highlighted */}
      <section className="py-24 md:py-32 bg-white" data-testid="main-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('servicesPage.highlightsTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
              {t('servicesPage.highlightsTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={service.path}
                  className="block h-full bg-stone-50 border border-stone-200 rounded-sm p-8 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                  data-testid={`main-service-${index}`}
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-sm bg-emerald flex items-center justify-center text-white flex-shrink-0 group-hover:bg-bronze transition-colors">
                      {service.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-serif font-semibold text-stone-900 mb-3">
                        {t(service.titleKey)}
                      </h3>
                      <p className="text-stone-600 mb-4">
                        {t(service.descKey)}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-bronze group-hover:gap-3 transition-all">
                        {t('servicesPage.viewDetails')} <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Consulting Services */}
      <section className="py-24 md:py-32 bg-emerald" data-testid="consulting-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
              {t('servicesPage.consultingTag')}
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white">
              {t('servicesPage.consultingTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {consultingServices.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm hover:bg-white/20 transition-colors"
                data-testid={`consulting-service-${index}`}
              >
                <div className="w-12 h-12 rounded-sm bg-bronze/20 flex items-center justify-center text-bronze mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg font-serif font-semibold text-white mb-2">
                  {t(service.titleKey)}
                </h3>
                <p className="text-stone-300 text-sm">
                  {t(service.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Services */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg" data-testid="digital-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 flex items-center gap-6">
            <div className="h-16 w-auto overflow-hidden rounded">
              <img 
                src={HAS_LOGO_URL} 
                alt="HAS - Hold Account Service" 
                className="h-16 w-auto object-contain"
              />
            </div>
            <div>
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-2 block">
                {t('servicesPage.digitalTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">
                {t('servicesPage.digitalTitle')}
              </h2>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {digitalServices.map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-6 rounded-sm border border-stone-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`digital-service-${index}`}
              >
                <div className="w-10 h-10 rounded-sm bg-emerald/10 flex items-center justify-center text-emerald mb-4">
                  {service.icon}
                </div>
                <h3 className="font-serif font-semibold text-stone-900 mb-2">
                  {t(service.titleKey)}
                </h3>
                <p className="text-stone-600 text-sm">
                  {t(service.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-bronze font-medium text-sm tracking-wider uppercase mb-4 block">
                {t('servicesPage.techTag')}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
                {t('servicesPage.techTitle')}
              </h2>
              <p className="text-stone-600 mb-6 leading-relaxed">
                {t('servicesPage.techDesc')}
              </p>
              <ul className="space-y-4">
                {[
                  t('servicesPage.techFeature1'),
                  t('servicesPage.techFeature2'),
                  t('servicesPage.techFeature3'),
                  t('servicesPage.techFeature4')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-stone-700">
                    <span className="w-2 h-2 rounded-full bg-bronze" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-stone-100 p-8 rounded-sm"
            >
              <div className="bg-emerald text-white p-6 rounded-sm mb-4">
                <code className="text-sm font-mono">
                  {`// Simple and secure integration`}<br/>
                  {`const holder = new HolderAPI({`}<br/>
                  {`  apiKey: 'your-key',`}<br/>
                  {`  environment: 'production'`}<br/>
                  {`});`}
                </code>
              </div>
              <p className="text-stone-600 text-sm">
                {t('servicesPage.techCode')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-stone-50 noise-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              {t('servicesPage.ctaTitle')}
            </h2>
            <p className="text-stone-600 text-lg mb-8">
              {t('servicesPage.ctaDesc')}
            </p>
            <Button 
              asChild
              className="bg-emerald hover:bg-emerald-light text-white rounded-sm px-8 py-6 text-lg font-serif btn-shine"
              data-testid="servicos-cta-btn"
            >
              <Link to="/contato" className="flex items-center gap-2">
                {t('servicesPage.ctaBtn')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Servicos;
